import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BillingRangePreset, BillingReport, BillingVendorDto, BillingVendorId } from '@jordan/types';
import {
  Alert,
  type AppLayoutApi,
  Breadcrumb,
  Button,
  PageHeader,
  SecondarySidebar,
  SecondarySidebarSection,
  SelectMenu,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';

const RANGES: { value: BillingRangePreset; label: string }[] = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-30d', label: 'Last 30 days' },
  { value: 'last-90d', label: 'Last 90 days' },
];

type VendorChoice = 'all' | BillingVendorId;

// Stable per-series key shared by the stacked bars and the service table (mirrors the API's key).
const seriesKey = (vendor: string, service: string) => `${vendor}::${service}`;

// Distinct, repeatable palette assigned to services in subtotal order.
const PALETTE = [
  '#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4',
  '#d946ef', '#eab308', '#22c55e', '#3b82f6',
];

export function BillingPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<BillingVendorDto[]>([]);
  const [vendor, setVendor] = useState<VendorChoice>('all');
  const [range, setRange] = useState<BillingRangePreset>('this-month');
  const [report, setReport] = useState<BillingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  // Series hidden from the chart (still counted in totals). Keyed by `${vendor}::${service}`.
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const fmtCur = useCallback(
    (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: report?.currency || 'USD' }).format(n),
    [report?.currency],
  );
  const vendorLabel = useMemo(() => {
    const m = new Map(vendors.map((v) => [v.id as string, v.label]));
    return (id: string) => m.get(id) ?? id;
  }, [vendors]);

  useEffect(() => {
    apiGet<BillingVendorDto[]>('/api/billing/vendors').then(setVendors).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    setNotConfigured(false);
    setReport(null);
    setHidden(new Set()); // start with every series visible for the new view
    apiGet<BillingReport>(`/api/billing/report?vendor=${vendor}&range=${range}`)
      .then(setReport)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) setNotConfigured(true);
        else setErr(e instanceof Error ? e.message : 'Failed to load the report.');
      })
      .finally(() => setLoading(false));
  }, [vendor, range]);

  const showVendorCol = vendor === 'all';
  const showForecast = report?.forecastCost != null;
  const title = vendor === 'all' ? 'All vendors' : vendorLabel(vendor);

  // Colour per series, assigned in the service table's order so bars and swatches agree.
  const colorFor = useMemo(() => {
    const m = new Map<string, string>();
    (report?.services ?? []).forEach((s, i) => m.set(seriesKey(s.vendor, s.service), PALETTE[i % PALETTE.length]));
    return (key: string) => m.get(key) ?? '#94a3b8';
  }, [report?.services]);

  const isVisible = useCallback((key: string) => !hidden.has(key), [hidden]);
  const toggleSeries = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Chart scale uses only the visible series so hiding one rescales the bars.
  const maxCost = useMemo(() => {
    if (!report) return 1;
    const visibleKeys = report.services.map((s) => seriesKey(s.vendor, s.service)).filter((k) => !hidden.has(k));
    const totals = report.days.map((d) => visibleKeys.reduce((sum, k) => sum + (d.segments[k] ?? 0), 0));
    return Math.max(...totals, 0.0001);
  }, [report, hidden]);

  const secondarySidebar = (api: AppLayoutApi) => {
    const choices: { id: VendorChoice; label: string; configured: boolean | null }[] = [
      { id: 'all', label: 'All vendors', configured: null },
      ...vendors.map((v) => ({ id: v.id, label: v.label, configured: v.configured })),
    ];
    return (
      <SecondarySidebar title="Billing" mobileExpanded={api.secondarySidebarExpanded} onClose={api.closeSecondarySidebar}>
        <SecondarySidebarSection title="Vendor" flush>
          <ul className="nav nav-sidebar my-2" data-nav-type="accordion">
            {choices.map((c) => (
              <li className="nav-item" key={c.id}>
                <a
                  href="#"
                  className={`nav-link ${vendor === c.id ? 'active' : ''}`.trim()}
                  onClick={(e) => {
                    e.preventDefault();
                    setVendor(c.id);
                    api.closeSecondarySidebar();
                  }}
                >
                  <span>{c.label}</span>
                  {c.configured === false && (
                    <i className="ph-warning-circle ms-auto text-warning" title="Not configured" />
                  )}
                  {c.configured === true && <i className="ph-check-circle ms-auto text-success" title="Configured" />}
                </a>
              </li>
            ))}
          </ul>
        </SecondarySidebarSection>
        <SecondarySidebarSection title="Date range">
          <SelectMenu
            className="mb-0"
            icon="ph-calendar-blank"
            value={range}
            onChange={(v) => setRange(v as BillingRangePreset)}
            options={RANGES}
          />
        </SecondarySidebarSection>
      </SecondarySidebar>
    );
  };

  return (
    <AppChrome secondarySidebar={secondarySidebar}>
      <PageHeader
        title={<>Billing - <span className="fw-normal">{title}</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Billing' }, { label: title }]} />}
        actions={<Button variant="light" icon="ph-gear" onClick={() => navigate('/configuration/billing')}>Settings</Button>}
      />
      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {loading && !report ? (
          <Spinner />
        ) : notConfigured ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ph-plug d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">{title} cost reporting isn’t configured yet</h5>
              <p className="text-muted mb-3">
                {vendor === 'google-cloud'
                  ? 'Add your Cloud Billing BigQuery export table under Settings.'
                  : vendor === 'aws'
                    ? 'Set AWS credentials (an IAM key with Cost Explorer access) in the environment (Secret Manager).'
                    : 'Set the provider’s API key in the environment (Secret Manager) so AI usage can be tracked and priced.'}
              </p>
              <Button variant="primary" icon="ph-gear" onClick={() => navigate('/configuration/billing')}>
                Billing settings
              </Button>
            </div>
          </div>
        ) : report ? (
          <>
            {/* Summary cards */}
            <div className="row g-3 mb-3">
              <div className={report.forecastCost != null ? 'col-md-6' : 'col-12'}>
                <div className="card h-100">
                  <div className="card-body">
                    <div className="text-muted">{report.rangeLabel} (total cost)</div>
                    <h3 className="fw-bold my-1">{fmtCur(report.totalCost)}</h3>
                    {report.savings !== 0 && (
                      <div className="text-muted fs-sm">includes {fmtCur(report.savings)} in savings</div>
                    )}
                  </div>
                </div>
              </div>
              {report.forecastCost != null && (
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="text-muted">{report.monthLabel} (forecasted total cost)</div>
                      <h3 className="fw-bold my-1">{fmtCur(report.forecastCost)}</h3>
                      <div className="text-muted fs-sm">Per-service recurring run-rate; one-off charges excluded.</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Daily cost chart — stacked by service */}
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center">
                <h5 className="card-title mb-0">Daily cost</h5>
                <div className="ms-auto fs-sm text-muted">Faded bars are forecast</div>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-end gap-1" style={{ height: 220 }}>
                  {report.days.map((d) => {
                    // Stack visible services in the table's order; forecast days reuse their segments.
                    const segs = report.services
                      .map((s) => {
                        const key = seriesKey(s.vendor, s.service);
                        return { key, cost: d.segments[key] ?? 0, color: colorFor(key), label: s.service };
                      })
                      .filter((s) => s.cost > 0 && isVisible(s.key));
                    const visibleTotal = segs.reduce((sum, s) => sum + s.cost, 0);
                    return (
                      <div
                        key={d.date}
                        className="flex-fill"
                        title={`${d.date}${d.forecast ? ' (forecast)' : ''}: ${fmtCur(visibleTotal)}`}
                        style={{ height: '100%', display: 'flex', flexDirection: 'column-reverse', opacity: d.forecast ? 0.4 : 1 }}
                      >
                        {segs.map((s) => (
                          <div
                            key={s.key}
                            title={`${d.date} · ${s.label}: ${fmtCur(s.cost)}`}
                            style={{
                              height: `${(s.cost / maxCost) * 100}%`,
                              minHeight: s.cost > 0 ? 1 : 0,
                              background: s.color,
                            }}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex gap-1 mt-1">
                  {report.days.map((d) => (
                    <div key={d.date} className="flex-fill text-center text-muted" style={{ fontSize: 10 }}>
                      {Number(d.date.slice(-2))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Per-service breakdown */}
            <div className="card">
              <div className="card-header"><h5 className="card-title mb-0">Cost by service</h5></div>
              <div className="table-responsive">
                <table className="table text-nowrap mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          title="Toggle all series in the chart"
                          checked={report.services.length > 0 && hidden.size === 0}
                          ref={(el) => {
                            if (el) el.indeterminate = hidden.size > 0 && hidden.size < report.services.length;
                          }}
                          onChange={() =>
                            setHidden((prev) =>
                              prev.size === 0
                                ? new Set(report.services.map((s) => seriesKey(s.vendor, s.service)))
                                : new Set(),
                            )
                          }
                        />
                      </th>
                      {showVendorCol && <th>Vendor</th>}
                      <th>Service</th>
                      <th className="text-end">Usage cost</th>
                      <th className="text-end">Savings</th>
                      <th className="text-end">Subtotal</th>
                      {showForecast && <th className="text-end">Forecast</th>}
                      <th className="text-end">% Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.services.map((s, i) => {
                      const key = seriesKey(s.vendor, s.service);
                      return (
                        <tr key={`${s.vendor}-${s.service}-${i}`}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isVisible(key)}
                              onChange={() => toggleSeries(key)}
                              title={isVisible(key) ? 'Hide from chart' : 'Show in chart'}
                            />
                          </td>
                          {showVendorCol && <td className="text-muted">{vendorLabel(s.vendor)}</td>}
                          <td className="fw-semibold">
                            <span
                              className="d-inline-block rounded-1 me-2"
                              style={{ width: 10, height: 10, background: colorFor(key), verticalAlign: 'middle' }}
                            />
                            {s.service}
                          </td>
                          <td className="text-end">{fmtCur(s.usageCost)}</td>
                          <td className="text-end text-muted">{s.savings !== 0 ? fmtCur(s.savings) : '—'}</td>
                          <td className="text-end fw-semibold">{fmtCur(s.subtotal)}</td>
                          {showForecast && (
                            <td className="text-end">{s.forecast != null ? fmtCur(s.forecast) : '—'}</td>
                          )}
                          <td className="text-end">
                            {s.pctChange === null ? (
                              <span className="text-muted">New</span>
                            ) : (
                              <span className={s.pctChange < 0 ? 'text-success' : s.pctChange > 0 ? 'text-danger' : 'text-muted'}>
                                <i className={`me-1 ${s.pctChange < 0 ? 'ph-arrow-down' : s.pctChange > 0 ? 'ph-arrow-up' : ''}`} />
                                {Math.abs(s.pctChange).toFixed(0)}%
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {report.services.length === 0 && (
                      <tr>
                        <td colSpan={3 + (showVendorCol ? 1 : 0) + (showForecast ? 1 : 0) + 2} className="text-muted">
                          No costs recorded for this period yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="border-top">
                    <tr>
                      <th colSpan={2 + (showVendorCol ? 1 : 0)}>Total</th>
                      <th />
                      <th className="text-end text-muted">{report.savings !== 0 ? fmtCur(report.savings) : '—'}</th>
                      <th className="text-end">{fmtCur(report.totalCost)}</th>
                      {showForecast && <th className="text-end">{fmtCur(report.forecastCost ?? 0)}</th>}
                      <th />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AppChrome>
  );
}
